import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'

interface BookOption {
  id: number
  book: {
    title: string
    author: string
    cover_url?: string
    description?: string
  }
}

interface ProposalData {
  id: number
  club_name: string
  proposer_name: string
  voting_deadline: string
  deadline_passed: boolean
  options: BookOption[]
  vote_count: number
}

export default function PublicVote() {
  const { token } = useParams<{ token: string }>()
  const [proposal, setProposal] = useState<ProposalData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [guestName, setGuestName] = useState('')
  const [rankedOptions, setRankedOptions] = useState<BookOption[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState('')

  useEffect(() => {
    fetchProposal()
  }, [token])

  const fetchProposal = async () => {
    try {
      const response = await fetch(`/api/v1/vote/${token}`)
      if (response.ok) {
        const data = await response.json()
        setProposal(data.proposal)
        setRankedOptions(data.proposal.options)
      } else {
        const data = await response.json()
        setError(data.error || 'Vote not found')
      }
    } catch {
      setError('Failed to load vote')
    } finally {
      setLoading(false)
    }
  }

  const moveOption = (fromIndex: number, toIndex: number) => {
    const newRanked = [...rankedOptions]
    const [movedItem] = newRanked.splice(fromIndex, 1)
    newRanked.splice(toIndex, 0, movedItem)
    setRankedOptions(newRanked)
  }

  const handleSubmit = async () => {
    if (!guestName.trim()) {
      setSubmitError('Please enter your name')
      return
    }

    setSubmitting(true)
    setSubmitError('')

    try {
      const rankings: { [key: string]: number } = {}
      rankedOptions.forEach((option, index) => {
        rankings[option.id.toString()] = index + 1
      })

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

      const response = await fetch(`/api/v1/vote/${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {})
        },
        body: JSON.stringify({ guest_name: guestName.trim(), rankings })
      })

      if (response.ok) {
        setSubmitted(true)
      } else {
        const data = await response.json()
        setSubmitError(data.error || 'Failed to submit vote')
      }
    } catch {
      setSubmitError('Failed to submit vote')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">{error}</h2>
          <p className="text-gray-600 mb-6">This voting link may have expired or been completed.</p>
          <Link to="/" className="text-indigo-600 hover:text-indigo-700 font-medium">
            Go to homepage
          </Link>
        </div>
      </div>
    )
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Vote submitted!</h2>
          <p className="text-gray-600 mb-2">Thanks for voting, {guestName}!</p>
          <p className="text-sm text-gray-500">The results will be tallied after the voting deadline.</p>
        </div>
      </div>
    )
  }

  if (!proposal) return null

  const deadlineDate = new Date(proposal.voting_deadline + 'T23:59:59')
  const formattedDeadline = deadlineDate.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  })

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Book Vote</h1>
          <p className="text-gray-600">
            <span className="font-medium">{proposal.proposer_name}</span> wants{' '}
            <span className="font-medium">{proposal.club_name}</span> to pick the next book
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {proposal.deadline_passed ? (
              <span className="text-red-600 font-medium">Voting has ended</span>
            ) : (
              <>Vote by {formattedDeadline}</>
            )}
          </p>
          <p className="text-sm text-gray-400 mt-1">{proposal.vote_count} vote{proposal.vote_count !== 1 ? 's' : ''} so far</p>
        </div>

        {proposal.deadline_passed ? (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <p className="text-gray-600">The voting deadline has passed. Check back with your book club for the results!</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Name input */}
            <div className="p-6 border-b border-gray-200">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Your Name</label>
              <input
                type="text"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                placeholder="Enter your name"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-0 outline-none transition-colors text-gray-900"
              />
            </div>

            {/* Ranking */}
            <div className="p-6">
              <p className="text-sm font-semibold text-gray-700 mb-1">Rank Your Choices</p>
              <p className="text-sm text-gray-500 mb-4">Use the arrows to reorder. Your #1 choice gets the most points.</p>

              <div className="space-y-3">
                {rankedOptions.map((option, index) => (
                  <div
                    key={option.id}
                    className="flex items-center gap-4 p-4 bg-gray-50 border-2 border-gray-200 rounded-xl hover:border-indigo-300 transition-all"
                  >
                    {/* Rank Number */}
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 text-white font-bold flex items-center justify-center text-lg">
                        {index + 1}
                      </div>
                    </div>

                    {/* Book Info */}
                    <div className="flex gap-3 flex-1 min-w-0">
                      {option.book.cover_url ? (
                        <img
                          src={option.book.cover_url}
                          alt={option.book.title}
                          className="w-12 h-18 object-cover rounded shadow-sm flex-shrink-0"
                        />
                      ) : (
                        <div className="w-12 h-18 bg-gradient-to-br from-indigo-100 to-purple-100 rounded flex items-center justify-center flex-shrink-0">
                          <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900">{option.book.title}</h3>
                        <p className="text-sm text-gray-600">by {option.book.author}</p>
                      </div>
                    </div>

                    {/* Move Buttons */}
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => moveOption(index, index - 1)}
                        disabled={index === 0}
                        className="p-1 text-gray-400 hover:text-indigo-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        title="Move up"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      </button>
                      <button
                        onClick={() => moveOption(index, index + 1)}
                        disabled={index === rankedOptions.length - 1}
                        className="p-1 text-gray-400 hover:text-indigo-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        title="Move down"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Explainer */}
              <div className="mt-6 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                <div className="flex gap-3">
                  <svg className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="text-sm text-indigo-900">
                    <p className="font-semibold mb-1">How ranked-choice voting works:</p>
                    <p className="text-indigo-800">
                      Your #1 choice gets {rankedOptions.length} points, #2 gets {rankedOptions.length - 1} points, and so on.
                      The book with the most total points wins!
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="p-6 border-t border-gray-200">
              {submitError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm">{submitError}</p>
                </div>
              )}
              <button
                onClick={handleSubmit}
                disabled={submitting || !guestName.trim()}
                className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-indigo-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/30"
              >
                {submitting ? 'Submitting...' : 'Submit Vote'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
